import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';

export function AdminServiceTest({ headers }) {
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMessage, setSmsMessage] = useState('Test SMS from PayMig Admin');
  const [smsSending, setSmsSending] = useState(false);

  const [emailAddr, setEmailAddr] = useState('');
  const [emailMessage, setEmailMessage] = useState('Test email from PayMig Admin');
  const [emailSending, setEmailSending] = useState(false);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await axios.get(`${API}/admin/test/logs`, { headers });
      setLogs(res.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [headers]);

  const sendTestSms = async () => {
    if (!smsPhone.trim()) return toast.error('Enter phone number');
    if (!smsMessage.trim()) return toast.error('Enter message');
    try {
      setSmsSending(true);
      const res = await axios.post(`${API}/admin/test/sms`, { phone: smsPhone, message: smsMessage }, { headers });
      if (res.data.status === 'sent') {
        toast.success(`SMS sent to ${res.data.phone}`);
      } else {
        toast.error(`SMS failed: ${res.data.error}`);
      }
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSmsSending(false);
    }
  };

  const sendTestEmail = async () => {
    if (!emailAddr.trim()) return toast.error('Enter email address');
    if (!emailMessage.trim()) return toast.error('Enter message');
    try {
      setEmailSending(true);
      const res = await axios.post(`${API}/admin/test/email`, { email: emailAddr, message: emailMessage }, { headers });
      if (res.data.status === 'sent') {
        toast.success(`Email sent to ${res.data.email}`);
      } else {
        toast.error(`Email failed: ${res.data.error}`);
      }
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-service-test">
      <h2 className="text-xl font-semibold">Service Test</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* SMS Test */}
        <Card data-testid="sms-test-card">
          <CardHeader>
            <CardTitle className="text-base">SMS Service Test</CardTitle>
            <CardDescription>Send a test SMS via 46elks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Phone Number</Label>
              <Input value={smsPhone} onChange={e => setSmsPhone(e.target.value)} placeholder="07XXXXXXXX" data-testid="sms-test-phone" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={smsMessage} onChange={e => setSmsMessage(e.target.value)} rows={3} data-testid="sms-test-message" />
            </div>
            <Button onClick={sendTestSms} disabled={smsSending} className="w-full" data-testid="send-test-sms-btn">
              {smsSending ? 'Sending...' : 'Send Test SMS'}
            </Button>
          </CardContent>
        </Card>

        {/* Email Test */}
        <Card data-testid="email-test-card">
          <CardHeader>
            <CardTitle className="text-base">Email Service Test</CardTitle>
            <CardDescription>Send a test email via SMTP</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Email Address</Label>
              <Input type="email" value={emailAddr} onChange={e => setEmailAddr(e.target.value)} placeholder="test@example.com" data-testid="email-test-address" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={3} data-testid="email-test-message" />
            </div>
            <Button onClick={sendTestEmail} disabled={emailSending} className="w-full" data-testid="send-test-email-btn">
              {emailSending ? 'Sending...' : 'Send Test Email'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Logs */}
      <Card data-testid="service-test-logs">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Service Logs</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchLogs} disabled={logsLoading}>Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No logs yet</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto">
              {logs.map((log, i) => (
                <div key={log.id || i} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg text-sm" data-testid={`service-log-${i}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant={log.status === 'SENT' ? 'default' : 'destructive'} className="text-xs">{log.status}</Badge>
                      <span className="font-medium">{log.type === 'sms_test' ? 'SMS' : 'Email'}</span>
                      <span className="text-muted-foreground">{log.phone || log.email}</span>
                    </div>
                    {log.error && <p className="text-xs text-rose-600 mt-1">{log.error}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{log.message?.substring(0, 80)}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{formatDate(log.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
